import * as React from "react";
import {
  Table,
  InputNumber,
  Button,
  Space,
  Radio,
  Divider,
  Spin,
  PageHeader,
  Layout,
  Descriptions,
  Form,
  Input,
  Empty,
  Card,
  Progress,
  Popover,
  Row,
  Alert,
  Skeleton,
  Checkbox,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { RouteComponentProps } from "react-router-dom";
import * as IssuedOrderStore from "../store/IssuedOrder";
import { connect } from "react-redux";
import { ApplicationState } from "../store";
import { DeleteFilled, PrinterOutlined } from "@ant-design/icons";
import {
  IntakeEntry,
  PrintClient,
  ReceivedOrderProduct,
  SkuWeightKind,
} from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage, type ApiError } from "../utils/apiError";
import { NumericKeyboard } from "./shared/NumericKeyboard";
import {
  getPersistedSessionProducts,
  sessionHasRestorableChanges,
  type StoredProduct,
} from "./order-intake-persistence";

// At runtime, Redux will merge together...
type OrderIntakeProps = IssuedOrderStore.IssuedOrderState & // ... state we've requested from the Redux store
  typeof IssuedOrderStore.actionCreators & // ... plus action creators we've requested
  RouteComponentProps<{ selectedOrderNumber: string }>; // ... plus incoming routing parameters

// Initialize the print client
const printClient = new PrintClient(new ApiConfig());

// LocalStorage key prefix for order intake sessions
const STORAGE_KEY_PREFIX = "order-intake-session-";

interface StoredSession {
  orderNumber: string;
  products: StoredProduct[];
  missingGoodsWillBeDeliveredLater: boolean;
}

type PrinterPort = "6101" | "6102";

const printerOptions: { label: string; value: PrinterPort }[] = [
  { label: "SKLAD", value: "6101" },
  { label: "HALA", value: "6102" },
];

const print = (
  productName: string,
  productCode: string,
  batchNumber: string | undefined,
  amount: number,
  unit: string,
  printerPort?: PrinterPort
) => {
  // Note: The printer parameter is not currently supported by the API client but kept for future enhancement
  const reasonForPrinting = ""; // TODO: Add reason for printing parameter
  const printingUser = ""; // TODO: Add printing user parameter

  printClient
    .etiquette(
      productName,
      productCode,
      batchNumber ?? undefined,
      amount.toString(),
      unit,
      printerPort ? parseInt(printerPort, 10) : undefined
    )
    .catch((error: ApiError) => {
      console.error("Failed to print label:", getApiErrorMessage(error));
    });
};

const OrderIntake = (props: OrderIntakeProps) => {
  var { requestIssuedOrder, issuedOrder } = props;
  const orderNumberFromUrl = props.match.params.selectedOrderNumber;

  // Helper function to get background color for a product based on intake progress
  const getProductBackground = (product: ReceivedOrderProduct) => {
    const currentCount = product.IntakeEntries.reduce(
      (total, e) => total + e.QuantityReceived,
      product.QuantityDelivered
    );
    const percentage = Math.min(Math.floor((currentCount / product.QuantityOrdered) * 100), 100);

    if (currentCount > product.QuantityOrdered) {
      return "#ff4d4f"; // Overpacked: red
    } else if (currentCount === product.QuantityOrdered) {
      return "#52c51a"; // Complete: green
    } else {
      // Partial: gradient showing progress
      return `linear-gradient(90deg, #1990ff 0%, #1990ff ${percentage}%, #fff ${percentage}%, #fff 100%)`;
    }
  };

  // Flag to prevent saving during initial load
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [hasRestoredFromStorage, setHasRestoredFromStorage] = React.useState(false);
  const [showRestorePrompt, setShowRestorePrompt] = React.useState(false);
  const [savedSession, setSavedSession] = React.useState<StoredSession | null>(null);

  // Storage key for this specific order
  const storageKey = React.useMemo(
    () => `${STORAGE_KEY_PREFIX}${orderNumberFromUrl}`,
    [orderNumberFromUrl]
  );

  React.useEffect(() => {
    // If we have everything loaded, we don't need to load again
    // This is especially important when navigating back from "Unable to create order receipt" page
    if (orderNumberFromUrl != issuedOrder?.Number) {
      requestIssuedOrder(orderNumberFromUrl);
    }
  }, [requestIssuedOrder, issuedOrder, orderNumberFromUrl]); // request order on mount

  // Check for saved data in localStorage after order is loaded
  React.useEffect(() => {
    if (!issuedOrder || hasRestoredFromStorage || props.isLoading) {
      return;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) {
        setIsInitialLoad(false);
        setHasRestoredFromStorage(true);
        return;
      }

      const session: StoredSession = JSON.parse(saved);

      if (sessionHasRestorableChanges(session.products, issuedOrder.Items)) {
        // Show restore prompt instead of auto-restoring
        setSavedSession(session);
        setShowRestorePrompt(true);
      }

    } catch (error) {
      console.error("Failed to load session from localStorage:", error);
    } finally {
      setIsInitialLoad(false);
      setHasRestoredFromStorage(true);
    }
  }, [issuedOrder, hasRestoredFromStorage, props.isLoading, storageKey]);

  // Handler to restore data from saved session
  const handleRestoreSession = () => {
    if (!savedSession) return;

    try {
      // Reconstruct receivedItems with restored data
      const restoredItems = props.receivedItems.map(product => {
        const storedProduct = savedSession.products.find(
          p => p.StockItemId === product.StockItemId
        );

        if (storedProduct) {
          return {
            ...product,
            WeightG: storedProduct.WeightG,
            WeightKind: storedProduct.WeightKind ?? SkuWeightKind.Estimated,
            IntakeEntries: storedProduct.IntakeEntries.map((entry, index) => ({
              Id: index,
              QuantityReceived: entry.QuantityReceived,
              BatchNumber: entry.BatchNumber,
            })),
          };
        }

        return product;
      });

      // Restore all data at once using the new action
      props.restoreFromLocalStorage(restoredItems, savedSession.missingGoodsWillBeDeliveredLater);

      // Hide the prompt
      setShowRestorePrompt(false);
      setSavedSession(null);
    } catch (error) {
      console.error("Failed to restore session:", error);
    }
  };

  // Handler to dismiss the restore prompt
  const handleDismissPrompt = () => {
    setShowRestorePrompt(false);
    setSavedSession(null);
    // Optionally clear the saved session from localStorage
    localStorage.removeItem(storageKey);
  };

  // Auto-save to localStorage when state changes
  React.useEffect(() => {
    if (isInitialLoad || !issuedOrder) {
      return;
    }

    try {
      const products = getPersistedSessionProducts(props.receivedItems, issuedOrder.Items);

      if (products.length === 0 && !props.missingGoodsWillBeDeliveredLater) {
        // Nothing to save, clear storage
        localStorage.removeItem(storageKey);
        return;
      }

      const session: StoredSession = {
        orderNumber: issuedOrder.Number,
        products,
        missingGoodsWillBeDeliveredLater: props.missingGoodsWillBeDeliveredLater,
      };

      localStorage.setItem(storageKey, JSON.stringify(session));
    } catch (error) {
      console.error("Failed to save session to localStorage:", error);
    }
  }, [
    isInitialLoad,
    issuedOrder,
    props.receivedItems,
    props.missingGoodsWillBeDeliveredLater,
    storageKey,
  ]);

  // Clear localStorage when navigating away to receipt creation
  React.useEffect(() => {
    // Listen for successful receipt creation (when receivedItems are cleared)
    if (hasRestoredFromStorage && !isInitialLoad && props.receivedItems.length === 0 && !props.isLoading) {
      localStorage.removeItem(storageKey);
    }
  }, [props.receivedItems, props.isLoading, hasRestoredFromStorage, isInitialLoad, storageKey]);

  const isOrderIncomplete = React.useMemo(() => {
    if (!issuedOrder) {
      return false;
    }

    return issuedOrder.Items.some((item) => {
      const receivedItem = props.receivedItems.find(
        (received) => received.IssuedOrderItemId === item.IssuedOrderItemId
      );

      const newlyReceived = receivedItem
        ? receivedItem.IntakeEntries.reduce(
            (total, entry) => total + entry.QuantityReceived,
            0
          )
        : 0;

      return item.QuantityOrdered !== item.QuantityDelivered + newlyReceived;
    });
  }, [issuedOrder, props.receivedItems]);

  React.useEffect(() => {
    if (!isOrderIncomplete && props.missingGoodsWillBeDeliveredLater) {
      props.setMissingGoodsWillBeDeliveredLater(false);
    }
  }, [
    isOrderIncomplete,
    props.missingGoodsWillBeDeliveredLater,
    props.setMissingGoodsWillBeDeliveredLater,
  ]);

  // Local UI state for printing and weights
  const [selectedPrinter, setSelectedPrinter] = React.useState<PrinterPort>(
    "6101"
  );
  const [containerSizeByProduct, setContainerSizeByProduct] = React.useState<{
    [stockItemId: number]: number;
  }>({});
  const [weightByProduct, setWeightByProduct] = React.useState<{
    [stockItemId: number]: number | undefined;
  }>({});
  const [pieceLabelCountByEntry, setPieceLabelCountByEntry] = React.useState<{
    [entryId: string]: number;
  }>({});
  const [containerLabelCountByEntry, setContainerLabelCountByEntry] = React.useState<{
    [entryId: string]: number;
  }>({});
  const [pieceLabelAmountByEntry, setPieceLabelAmountByEntry] = React.useState<{
    [entryId: string]: number;
  }>({});
  const [selectedPrintOptionByEntry, setSelectedPrintOptionByEntry] = React.useState<{
    [entryId: string]: string[];
  }>({});

  // Bulk print state for entire order
  const [bulkPrintPopoverVisible, setBulkPrintPopoverVisible] = React.useState(false);
  const [bulkPrintSelectedOptions, setBulkPrintSelectedOptions] = React.useState<string[]>([]);

  // Get all intake entries across all products
  const getAllIntakeEntries = () => {
    const result: { product: ReceivedOrderProduct; entry: IntakeEntry }[] = [];
    props.receivedItems.forEach((product) => {
      product.IntakeEntries.forEach((entry) => {
        result.push({ product, entry });
      });
    });
    return result;
  };

  const totalEntriesCount = getAllIntakeEntries().length;

  // Dummy backend call to save weight for a product
  const sendWeight = async (
    productCode: string,
    weight: number | undefined
  ) => {
    // TODO: Replace with real backend call
    console.log("sendWeight", { productCode, weight });
    return Promise.resolve();
  };

  const getContainerSize = (product: ReceivedOrderProduct) => {
    const override = containerSizeByProduct[product.StockItemId];
    if (override && override > 0) {
      return override;
    }

    const fromProduct = product.ContainerSize;
    return fromProduct && fromProduct > 0 ? fromProduct : 1;
  };
  const setContainerSize = (stockItemId: number, value?: number) =>
    setContainerSizeByProduct((prev) => ({
      ...prev,
      [stockItemId]: value && value > 0 ? value : 1,
    }));

  // Calculate print preview based on selected options
  const getBulkPrintPreview = () => {
    const allEntries = getAllIntakeEntries();
    const preview: { product: ReceivedOrderProduct; count: number; amount: number }[] = [];

    allEntries.forEach(({ product, entry }) => {
      const quantityReceived = entry.QuantityReceived || 0;
      const containerSize = getContainerSize(product);
      const numPackages = Math.ceil(quantityReceived / containerSize);

      bulkPrintSelectedOptions.forEach((optionId) => {
        let count = 0;
        let amount = 0;

        switch (optionId) {
          case "option1":
            count = 1;
            amount = quantityReceived;
            break;
          case "option2":
            count = numPackages;
            amount = containerSize;
            break;
          case "option5":
            count = 1;
            amount = 1;
            break;
        }

        for (let i = 0; i < count; i++) {
          preview.push({ product, count: 1, amount });
        }
      });
    });

    // Group by product code and amount
    const grouped: { [key: string]: { product: ReceivedOrderProduct; amount: number; count: number } } = {};
    preview.forEach(({ product, amount }) => {
      const key = `${product.Code}-${amount}`;
      if (!grouped[key]) {
        grouped[key] = { product, amount, count: 0 };
      }
      grouped[key].count++;
    });

    return Object.values(grouped);
  };

  const bulkPrintPreview = getBulkPrintPreview();
  const totalLabelsCount = bulkPrintPreview.reduce((sum, item) => sum + item.count, 0);

  const handleBulkPrintAll = () => {
    const allEntries = getAllIntakeEntries();

    bulkPrintSelectedOptions.forEach((optionId) => {
      allEntries.forEach(({ product, entry }) => {
        const quantityReceived = entry.QuantityReceived || 0;
        const containerSize = getContainerSize(product);
        const numPackages = Math.ceil(quantityReceived / containerSize);

        let count = 0;
        let amount = 0;

        switch (optionId) {
          case "option1":
            count = 1;
            amount = quantityReceived;
            break;
          case "option2":
            count = numPackages;
            amount = containerSize;
            break;
          case "option5":
            count = 1;
            amount = 1;
            break;
        }

        for (let i = 0; i < count; i++) {
          print(
            product.Name,
            product.Code,
            entry.BatchNumber,
            amount,
            product.Unit,
            selectedPrinter
          );
        }
      });
    });

    setBulkPrintPopoverVisible(false);
    setBulkPrintSelectedOptions([]);
  };

  React.useEffect(() => {
    setContainerSizeByProduct((prev) => {
      let changed = false;
      const next = { ...prev };
      props.receivedItems.forEach((product) => {
        if (next[product.StockItemId] == null) {
          const size = product.ContainerSize && product.ContainerSize > 0
            ? product.ContainerSize
            : 1;
          next[product.StockItemId] = size;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [props.receivedItems]);

  // Slovak pluralization for the noun "štítok"
  const skLabelWord = (count: number) => {
    const n = Math.abs(Math.floor(count));
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 14) return "štítkov";
    const mod10 = n % 10;
    if (mod10 === 1) return "štítok";
    if (mod10 >= 2 && mod10 <= 4) return "štítky";
    return "štítkov";
  };

  const printContainerLabels = (
    product: ReceivedOrderProduct,
    entry: IntakeEntry,
    overrideCount?: number
  ) => {
    const containerSize = getContainerSize(product);
    const count =
      overrideCount != null
        ? overrideCount
        : Math.ceil((entry.QuantityReceived || 0) / containerSize);
    const printerPort = selectedPrinter;
    for (let i = 0; i < count; i++) {
      print(
        product.Name,
        product.Code,
        entry.BatchNumber,
        containerSize,
        product.Unit,
        printerPort
      );
    }
  };

  const printPieceLabels = (
    product: ReceivedOrderProduct,
    entry: IntakeEntry,
    overrideCount?: number,
    amount?: number
  ) => {
    const count =
      overrideCount != null ? overrideCount : entry.QuantityReceived || 0;
    const labelAmount = amount != null ? amount : 1;
    const printerPort = selectedPrinter;
    for (let i = 0; i < count; i++) {
      print(
        product.Name,
        product.Code,
        entry.BatchNumber,
        labelAmount,
        product.Unit,
        printerPort
      );
    }
  };

  //<h1>
  //    Príjem objednávky <br />
  //    č. {props.match.params.selectedOrderNumber} <br />
  //    od { props.isLoading ? <Spin /> : props.issuedOrder?.CompanyName }
  //</h1>

  const FieldSkeleton = (fSProps: { widthEm?: number; widthCh?: number }) => (
    <Skeleton
      active={props.isLoading}
      title={false}
      paragraph={{
        rows: 1,
        width: fSProps.widthEm
          ? fSProps.widthEm + "em"
          : fSProps.widthCh + "ch",
        style: { margin: 0 },
      }}
    />
  );

  return (
    <React.Fragment>
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title={"Objednávka č. " + props.match.params.selectedOrderNumber}
        //subTitle={props.isLoading ? <Spin /> : "č. " + props.match.params.selectedOrderNumber + "od " + props.issuedOrder?.CompanyName}
      />

      <Descriptions size="default" column={{ md: 2, xs: 1 }} bordered>
        <Descriptions.Item label="Partner">
          {props.isLoading ? (
            <FieldSkeleton widthEm={8} />
          ) : (
            props.issuedOrder?.CompanyName
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Dátum vystavenia">
          {props.isLoading ? (
            <FieldSkeleton widthCh={new Date().toLocaleDateString().length} />
          ) : (
            new Date(props.issuedOrder?.DateIssued || "").toLocaleDateString()
          )}
        </Descriptions.Item>
      </Descriptions>

      <br />

      {props.error != "" && (
        <React.Fragment>
          <Alert message={props.error} type="error" />
          <br />
        </React.Fragment>
      )}

      {showRestorePrompt && (
        <React.Fragment>
          <Alert
            message="Obnoviť rozostavaný príjem?"
            description="Našla sa predchádzajúca rozpracovaná relácia príjmu tovaru. Chcete ju obnoviť?"
            type="info"
            action={
              <Space direction="horizontal">
                <Button size="small" type="primary" onClick={handleRestoreSession}>
                  Obnoviť
                </Button>
                <Button size="small" onClick={handleDismissPrompt}>
                  Zahodiť
                </Button>
              </Space>
            }
            closable
            onClose={handleDismissPrompt}
          />
          <br />
        </React.Fragment>
      )}

      <h2>Príjem tovaru</h2>

      <style>{`
        .ant-table-tbody > tr.ant-table-row > td,
        .ant-table-tbody > tr.ant-table-row:hover > td,
        .ant-table-tbody > tr.ant-table-row:active > td,
        .ant-table-tbody > tr.ant-table-row:focus > td,
        .ant-table-tbody > tr.ant-table-row-selected > td,
        .ant-table-tbody > tr.ant-table-row > td.ant-table-cell-row-hover {
          background: none !important;
        }
      `}</style>
      <Table
        loading={props.isLoading}
        dataSource={props.receivedItems}
        rowKey={(row) => row.StockItemId}
        onRow={(product: ReceivedOrderProduct) => ({
          style: { background: getProductBackground(product) }
        })}
        columns={[
          {
            title: "Produkt",
            render: (product: ReceivedOrderProduct) => (
              <React.Fragment>
                <strong>{product.Name}</strong>
                <br />
                {product.Code}
              </React.Fragment>
            ),
          },
          {
            title: "Množstvo",
            align: "right",
            render: (product: ReceivedOrderProduct) => {
              const currentSessionReceived = product.IntakeEntries.reduce(
                (total, e) => total + e.QuantityReceived,
                0
              );
              const totalReceived = product.QuantityDelivered + currentSessionReceived;
              return (
                <React.Fragment>
                  <div>{totalReceived} / {product.QuantityOrdered} {product.Unit}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    +{currentSessionReceived} {product.Unit}
                  </div>
                </React.Fragment>
              );
            },
          },
          //{
          //    title: "Objednaných",
          //    dataIndex: "QuantityOrdered"
          //},
          //{
          //    title: "Príjem tovaru",
          //    dataIndex: "IntakeEntries",
          //    render: (IntakeEntries: IntakeEntry[], product) =>
          //        <React.Fragment>
          //            <Input.Group>
          //                <Input type="number" size="middle" style={{ width: "50%", minWidth: "76px", textAlign: "right" }} suffix={product.Unit} value={props.printCount} onChange={(event) => props.changePrintCount(event.target.valueAsNumber)} />
          //                <Button size="middle" style={{ width: "25%", minWidth: "38px" }} onClick={() => props.changePrintCount(props.printCount - 1)}>-</Button>
          //                <Button size="middle" style={{ width: "25%", minWidth: "38px" }} onClick={() => props.changePrintCount(props.printCount + 1)}>+</Button>
          //            </Input.Group>

          //            {
          //            //<Button size="middle" style={{ width: "50px" }} onClick={() => { props.addIntakeEntry(props.intakeCount, product) }}>OK</Button>
          //            //<PlusOutlined style={{ color: "rgba(0, 0, 0, 0.85)" }} />
          //            //<MinusOutlined style={{ color: "rgba(0, 0, 0, 0.85)" }} />
          //            }

          //        </React.Fragment>

          //        //<Table
          //        //    dataSource={IntakeEntries}
          //        //    rowKey={row => row.Id}
          //        //    columns={[
          //        //        {
          //        //            dataIndex: "QuantityReceived", render: quantity => <React.Fragment>{quantity}&nbsp;{product.Unit}</React.Fragment>
          //        //        },
          //        //        { dataIndex: "Id", render: (id) => <Button danger shape="circle" icon={<DeleteFilled />} onClick={() => props.removeIntakeEntry(product, id)} /> }
          //        //    ]}
          //        //    pagination={false}
          //        //    showHeader={false}
          //        //    style={{ width: "100%" }}
          //        //    id="received-items-table"
          //        ///>,
          //    },
          {
            title: "",
            render: (product: ReceivedOrderProduct) => (
              <Popover
                title={product.Name}
                overlayStyle={{ width: "340px" }}
                content={
                  <React.Fragment>
                    <h2>Príjem tovaru</h2>

                    {props.loadingBatchNumberError != "" && (
                      <React.Fragment>
                        <Alert
                          message={props.loadingBatchNumberError}
                          type="error"
                        />
                        <br />
                      </React.Fragment>
                    )}

                    <Form
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      layout="vertical"
                    >
                      <Form.Item label={"Váha 1 " + product.Unit + " (g)"} wrapperCol={{ span: 24 }}>
                        <NumericKeyboard
                          title="Zadajte váhu"
                          unit="g"
                          value={product.WeightG ?? 0}
                          onChange={(value) => props.updateProductWeight(product.StockItemId, value == null ? null : value, product.WeightKind)}
                          onEnter={() => {
                            // Close popover
                          }}
                        >
                          <Button size="large" style={{ width: "100%", textAlign: "left" }}>
                            {product.WeightG != null ? `${product.WeightG} g` : "Zadajte váhu"}
                          </Button>
                        </NumericKeyboard>
                      </Form.Item>
                      <Form.Item label="Typ váhy" wrapperCol={{ span: 24 }}>
                        <Radio.Group
                          optionType="button"
                          buttonStyle="solid"
                          value={product.WeightKind}
                          onChange={(event) =>
                            props.updateProductWeight(
                              product.StockItemId,
                              product.WeightG,
                              event.target.value as SkuWeightKind
                            )
                          }
                        >
                          <Radio.Button value={SkuWeightKind.Estimated}>Odhad</Radio.Button>
                          <Radio.Button value={SkuWeightKind.Measured}>Odvážená</Radio.Button>
                        </Radio.Group>
                      </Form.Item>

                      <Form.Item
                        label={"Množstvo " + "(" + product.Unit + ")"}
                        name="x"
                      >
                        <Input.Group>
                          <NumericKeyboard
                            title="Zadajte množstvo"
                            unit={product.Unit}
                            value={props.intakeCount}
                            onChange={(value) => props.changeIntakeCount(value)}
                            onEnter={() => {
                              props.addIntakeEntry(props.intakeCount, product);
                            }}
                          >
                            <Button size="large" style={{ width: "70%", textAlign: "left" }}>
                              {props.intakeCount} {product.Unit}
                            </Button>
                          </NumericKeyboard>
                          <Button
                            size="large"
                            style={{ width: "15%" }}
                            onClick={() =>
                              props.changeIntakeCount(props.intakeCount + 1)
                            }
                          >
                            +
                          </Button>
                          <Button
                            size="large"
                            style={{ width: "15%" }}
                            onClick={() =>
                              props.changeIntakeCount(props.intakeCount - 1)
                            }
                          >
                            -
                          </Button>
                        </Input.Group>
                      </Form.Item>

                      <Form.Item wrapperCol={{ span: 24 }}>
                        <Button
                          disabled={props.intakeCount == 0}
                          loading={props.isLoadingBatchNumber}
                          size="large"
                          style={{ width: "100%" }}
                          onClick={() => {
                            props.addIntakeEntry(props.intakeCount, product);
                          }}
                        >
                          Prijať tovar
                        </Button>
                      </Form.Item>
                    </Form>

                    <h2>Prijatý tovar</h2>

                    <Table<IntakeEntry>
                      dataSource={product.IntakeEntries}
                      rowKey={(row) => row.Id}
                      showHeader={true}
                      columns={[
                        {
                          title: "Šarža",
                          dataIndex: "BatchNumber",
                          render: (BatchNumber) => (
                            <span style={{ whiteSpace: "nowrap" }}>
                              {BatchNumber}
                            </span>
                          ),
                        },
                        {
                          title: "Množstvo",
                          dataIndex: "QuantityReceived",
                          align: "right",
                          render: (quantityReceived) => (
                            <React.Fragment>
                              {quantityReceived}&nbsp;{product.Unit}
                            </React.Fragment>
                          ),
                        },
                        {
                          title: "",
                          align: "right",
                          render: (e: IntakeEntry) => {
                            const quantityReceived = e.QuantityReceived || 0;
                            const containerSize = getContainerSize(product);
                            const containerLabelCount =
                              quantityReceived > 0
                                ? Math.ceil(quantityReceived / containerSize)
                                : 0;

                            const pieceLabelCount = pieceLabelCountByEntry[e.Id] ?? quantityReceived;
                            const localContainerLabelCount = containerLabelCountByEntry[e.Id] ?? containerLabelCount;
                            const pieceLabelAmount = pieceLabelAmountByEntry[e.Id] ?? 1;

                            const selectedOptions = selectedPrintOptionByEntry[e.Id] || [];
                            const numPackages = Math.ceil(quantityReceived / containerSize);

                            // Define all print options with their descriptions
                            const printOptions = [
                              {
                                id: "option1",
                                title: "na celé množstvo",
                                description: `1 ${skLabelWord(1)} s množstvom ${quantityReceived} ${product.Unit}`,
                                count: 1,
                                amount: quantityReceived
                              },
                              {
                                id: "option2",
                                title: "na každé balenie",
                                description: `${numPackages} ${skLabelWord(numPackages)} s množstvom ${containerSize} ${product.Unit}`,
                                count: numPackages,
                                amount: containerSize
                              },
                              {
                                id: "option3",
                                title: `1 ${product.Unit} na každé balenie`,
                                description: `${numPackages} ${skLabelWord(numPackages)} s množstvom 1 ${product.Unit}`,
                                count: numPackages,
                                amount: 1
                              },
                              {
                                id: "option4",
                                title: "na každý kus",
                                description: `${quantityReceived} ${skLabelWord(quantityReceived)} s množstvom 1 ${product.Unit}`,
                                count: quantityReceived,
                                amount: 1
                              },
                              {
                                id: "option5",
                                title: "jeden štítok s 1ks",
                                description: `1 ${skLabelWord(1)} s množstvom 1 ${product.Unit}`,
                                count: 1,
                                amount: 1
                              }
                            ];

                            // Filter out duplicates based on count and amount
                            const uniqueOptions = printOptions.filter((option, index, self) =>
                              index === self.findIndex(o =>
                                o.count === option.count && o.amount === option.amount
                              )
                            );

                            const handlePopoverOpenChange = (open: boolean) => {
                              if (open) {
                                // Reset to defaults when opening
                                setSelectedPrintOptionByEntry((prev) => ({
                                  ...prev,
                                  [e.Id]: []
                                }));
                              }
                            };

                            const handlePrint = () => {
                              const printerPort = selectedPrinter;

                              selectedOptions.forEach(optionId => {
                                const option = printOptions.find(o => o.id === optionId);
                                if (option) {
                                  for (let i = 0; i < option.count; i++) {
                                    print(
                                      product.Name,
                                      product.Code,
                                      e.BatchNumber,
                                      option.amount,
                                      product.Unit,
                                      printerPort
                                    );
                                  }
                                }
                              });
                            };

                            return (
                              <Popover
                                trigger="click"
                                onOpenChange={handlePopoverOpenChange}
                                content={
                                  <Form
                                    layout="vertical"
                                    style={{ width: 280 }}
                                  >
                                  <Form.Item label="Tlačiareň">
                                    <Radio.Group
                                      options={printerOptions.map((option) => ({
                                        label: `${option.label} (${option.value})`,
                                        value: option.value,
                                      }))}
                                      optionType="button"
                                      buttonStyle="solid"
                                      value={selectedPrinter}
                                      onChange={(evt) =>
                                        setSelectedPrinter(
                                          evt.target.value as PrinterPort
                                        )
                                      }
                                    />
                                  </Form.Item>
                                  <Form.Item label="Veľkosť balenia">
                                    <NumericKeyboard
                                      title="Zadajte veľkosť balenia"
                                      unit="ks"
                                      value={getContainerSize(product)}
                                      onChange={(value) =>
                                        setContainerSize(
                                          product.StockItemId,
                                          value || 1
                                        )
                                      }
                                      onEnter={() => {
                                        // Close popover
                                      }}
                                    >
                                      <Button style={{ width: "100%", textAlign: "left" }}>
                                        {getContainerSize(product)} ks
                                      </Button>
                                    </NumericKeyboard>
                                  </Form.Item>
                                  <Form.Item label="Vytlačiť štítky">
                                    <Checkbox.Group
                                      value={selectedOptions}
                                      onChange={(checkedValues) => {
                                        setSelectedPrintOptionByEntry((prev) => ({
                                          ...prev,
                                          [e.Id]: checkedValues as string[]
                                        }));
                                      }}
                                    >
                                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                        {uniqueOptions.map(option => (
                                          <div key={option.id}>
                                            <Checkbox
                                              value={option.id}
                                              style={{ display: "flex", alignItems: "flex-start" }}
                                            >
                                              <div style={{ display: "flex", flexDirection: "column" }}>
                                                <div>{option.title}</div>
                                                <div style={{ fontSize: "12px", color: "#888" }}>
                                                  {option.description}
                                                </div>
                                              </div>
                                            </Checkbox>
                                          </div>
                                        ))}
                                      </div>
                                    </Checkbox.Group>
                                  </Form.Item>
                                  <Button
                                    block
                                    type="primary"
                                    onClick={handlePrint}
                                    disabled={selectedOptions.length === 0}
                                  >
                                    {selectedOptions.length === 0
                                      ? "Vytlačiť"
                                      : (() => {
                                          const totalCount = selectedOptions.reduce((sum, optionId) => {
                                            const option = printOptions.find(o => o.id === optionId);
                                            return sum + (option?.count || 0);
                                          }, 0);
                                          return `Vytlačiť ${totalCount} ${skLabelWord(totalCount)}`;
                                        })()
                                    }
                                  </Button>
                                </Form>
                              }
                            >
                              <Button
                                shape="circle"
                                icon={<PrinterOutlined />}
                              />
                              </Popover>
                            );
                          },
                        },
                        {
                          title: "",
                          align: "right",
                          render: (e: IntakeEntry) => (
                            <Button
                              danger
                              shape="circle"
                              icon={<DeleteFilled />}
                              onClick={() =>
                                props.removeIntakeEntry(product, e.Id)
                              }
                            />
                          ),
                        },
                      ]}
                      pagination={false}
                      style={{ width: "100%" }}
                      id="received-items-table"
                      locale={{
                        emptyText: (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Žiadny tovar"
                          />
                        ),
                      }}
                    />
                  </React.Fragment>
                }
                trigger="click"
              >
                <Button>Prijať</Button>
              </Popover>
            ),
          },
        ]}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Žiadny tovar"
            />
          ),
        }}
      />

      <br />

      {isOrderIncomplete && (
        <Form layout="horizontal">
          <Form.Item
            label="Bude chýbajúci tovar dodaný neskôr?"
            labelCol={{ span: 12 }}
            wrapperCol={{ span: 12 }}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={
                props.missingGoodsWillBeDeliveredLater ? "yes" : "no"
              }
              onChange={(event) =>
                props.setMissingGoodsWillBeDeliveredLater(
                  event.target.value === "yes"
                )
              }
            >
              <Radio.Button value="yes">Áno</Radio.Button>
              <Radio.Button value="no">Nie - označiť ako "Vybavené"</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <Popover
          trigger="click"
          open={bulkPrintPopoverVisible}
          onOpenChange={(open) => {
            if (totalEntriesCount > 0) {
              setBulkPrintPopoverVisible(open);
              if (open) {
                setBulkPrintSelectedOptions([]);
              }
            }
          }}
          content={
            <Form layout="vertical" style={{ width: 280 }}>
              <Form.Item label="Tlačiareň">
                <Radio.Group
                  options={printerOptions.map((option) => ({
                    label: `${option.label} (${option.value})`,
                    value: option.value,
                  }))}
                  optionType="button"
                  buttonStyle="solid"
                  value={selectedPrinter}
                  onChange={(evt) => setSelectedPrinter(evt.target.value as PrinterPort)}
                />
              </Form.Item>
              <Form.Item label="Vytlačiť štítky" style={{ marginBottom: 12 }}>
                <Checkbox.Group
                  value={bulkPrintSelectedOptions}
                  onChange={(checkedValues) => setBulkPrintSelectedOptions(checkedValues as string[])}
                  style={{ width: "100%" }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <Checkbox value="option1" style={{ marginLeft: 0 }}>
                      <div>
                        <div>na celé množstvo</div>
                        <div style={{ fontSize: "12px", color: "#888" }}>1 štítok na každý záznam</div>
                      </div>
                    </Checkbox>
                    <Checkbox value="option2" style={{ marginLeft: 0 }}>
                      <div>
                        <div>na každé balenie</div>
                        <div style={{ fontSize: "12px", color: "#888" }}>podľa veľkosti balenia produktu</div>
                      </div>
                    </Checkbox>
                    <Checkbox value="option5" style={{ marginLeft: 0 }}>
                      <div>
                        <div>jeden štítok s 1ks</div>
                        <div style={{ fontSize: "12px", color: "#888" }}>1 štítok na každý záznam</div>
                      </div>
                    </Checkbox>
                  </div>
                </Checkbox.Group>
              </Form.Item>
              {bulkPrintPreview.length > 0 && (
                <div style={{ marginBottom: 12, padding: 8, background: "#f5f5f5", borderRadius: 4, maxHeight: 150, overflowY: "auto" }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Prehľad ({totalLabelsCount} {totalLabelsCount === 1 ? "štítok" : totalLabelsCount < 5 ? "štítky" : "štítkov"}):</div>
                  {bulkPrintPreview.map((item, idx) => (
                    <div key={idx} style={{ fontSize: 12, color: "#666" }}>
                      {item.count}× {item.product.Code} ({item.amount} {item.product.Unit})
                    </div>
                  ))}
                </div>
              )}
              <Button
                block
                type="primary"
                onClick={handleBulkPrintAll}
                disabled={bulkPrintSelectedOptions.length === 0}
              >
                Vytlačiť {totalLabelsCount} {totalLabelsCount === 1 ? "štítok" : totalLabelsCount < 5 ? "štítky" : "štítkov"}
              </Button>
            </Form>
          }
        >
          <Button
            icon={<PrinterOutlined />}
            size="large"
            disabled={totalEntriesCount === 0}
          >
            Tlač štítkov ({totalEntriesCount})
          </Button>
        </Popover>
        <Button
          type="primary"
          disabled={
            props.receivedItems.reduce(
              (total, p) =>
                total +
                p.IntakeEntries.reduce(
                  (totalE, e) => totalE + e.QuantityReceived,
                  0
                ),
              0
            ) == 0
          }
          size="large"
          style={{ flex: 1 }}
          onClick={() =>
            props.history.push("/order-intake/20VOB0001/send-receipt")
          }
        >
          Vytvoriť príjemku
        </Button>
      </div>
    </React.Fragment>
  );
};

export default connect(
  (state: ApplicationState) => state.orderIntake, // Selects which state properties are merged into the component's props
  IssuedOrderStore.actionCreators // Selects which action creators are merged into the component's props
)(OrderIntake as any);

//<Form labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} layout="vertical">
//    <Form.Item label="Produkt" name="x">
//        <Select
//            showSearch
//            size="large"
//            style={{ width: "100%" }}
//            placeholder="Vyberte produkt"
//            optionFilterProp="children"
//            value={props.selectedProduct?.Code}
//            onChange={(value) => props.changeSelectedProduct(value)}
//            //onFocus={onFocus}
//            //onBlur={onBlur}
//            //onSearch={onSearch}
//            filterOption={(input, option) =>
//                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
//            }
//            loading={props.isLoading}
//        >
//            {props.issuedOrder?.Items?.map(orderedProduct =>
//                <Select.Option value={orderedProduct.Code} key={orderedProduct.Id}>{orderedProduct.Name}</Select.Option>
//            )}
//        </Select>
//    </Form.Item>

//    <Form.Item label={"Množstvo " + (props.selectedProduct != null ? "(" + props.selectedProduct?.Unit + ")" : "")} name="x">
//        <Input.Group>
//            <InputNumber size="large" style={{ width: "70%" }} value={props.intakeCount} onChange={(value) => props.changeIntakeCount(parseFloat(value?.toString() || "0"))} />
//            <Button size="large" style={{ width: "15%" }} onClick={() => props.changeIntakeCount(props.intakeCount + 1)}>+</Button>
//            <Button size="large" style={{ width: "15%" }} onClick={() => props.changeIntakeCount(props.intakeCount - 1)}>-</Button>
//        </Input.Group>
//    </Form.Item>

//    <Form.Item label="Vytlačiť štítky (ks)" name="Produkt">
//        <Input.Group>
//            <InputNumber size="large" style={{ width: "70%" }} value={props.printCount} onChange={(value) => props.changePrintCount(parseFloat(value?.toString() || "0"))} />
//            <Button size="large" style={{ width: "15%" }} onClick={() => props.changePrintCount(props.printCount + 1)}>+</Button>
//            <Button size="large" style={{ width: "15%" }} onClick={() => props.changePrintCount(props.printCount - 1)}>-</Button>
//        </Input.Group>
//    </Form.Item>

//    <Form.Item wrapperCol={{ span: 24 }}>
//        <Button size="large" style={{ width: "100%" }} onClick={() => { props.selectedProduct != null && props.addIntakeEntry(props.intakeCount, props.selectedProduct) }} disabled={props.selectedProduct == null}>Prijať tovar</Button>
//    </Form.Item>
//</Form>

//    <Divider />

//    <h2>Prijatý tovar</h2>

//    <Table dataSource={props.receivedItems} rowKey={row => row.Id} columns={[
//        {
//            title: "Produkt",
//            dataIndex: "Name"
//        },
//        {
//            title: "Počet prijatých",
//            dataIndex: "IntakeEntries",
//            render: (IntakeEntries: IntakeEntry[], product) =>
//                <Table
//                    dataSource={IntakeEntries}
//                    rowKey={row => row.Id}
//                    columns={[
//                        {
//                            dataIndex: "QuantityReceived", render: quantity => <React.Fragment>{quantity}&nbsp;{product.Unit}</React.Fragment>
//                        },
//                        { dataIndex: "Id", render: (id) => <Button danger shape="circle" icon={<DeleteFilled />} onClick={() => props.removeIntakeEntry(product, id)} /> }
//                    ]}
//                    pagination={false}
//                    showHeader={false}
//                    style={{ width: "100%" }}
//                    id="received-items-table"
//                />,
//        }
//    ]} pagination={false} locale={{
//        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Žiadny tovar" />,
//    }} />

//<h2>Príjem tovaru 3</h2>

//{props.issuedOrder?.Items.map(product =>
//    <Card title={product.Name}>
//        <Descriptions size="default" column={{ md: 2, xs: 1 }} bordered>
//            <Descriptions.Item label="Produkt">{product.Name}</Descriptions.Item>
//            <Descriptions.Item label="Zostáva">{product.QuantityOrdered - product.QuantityDelivered}</Descriptions.Item>
//            <Descriptions.Item label="Prijatých">{product.QuantityDelivered}</Descriptions.Item>
//        </Descriptions>
//    </Card>
//)}
